import { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Radio, Select, Space, Table, Tag, Typography } from "antd";
import { DeleteOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, StopOutlined, CheckOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import type { ColumnsType } from "antd/es/table";
import SweetAlert from "sweetalert2";
import axios from "axios";
import InputForm from "../../InputT";
import SelectOption from "../../SelectOption";
import CheckStan from "../../Prints/CheckStan";
import DataSelect from "../../../Types/DataSelect";
import { Crypter } from "../../../Functions/Security/Crypter";
import { OpeApi } from "../../../Services/ApiOpeLiees";
import { useAuthHeader } from "../../../Services/Auth/sessionAuth";

interface Condition {
    code: string;
    libelle: string;
    champ: string;
    operateur: string;
    valeur: string;
    valeur2?: string;
    listeval?: string;
    active: boolean;
    rowver?: string;   // base64, concurrence optimiste
}

interface GroupeCondition {
    id: number;            // IDGRP serveur (0 = nouveau non sauvegarde)
    codgrp: string;
    nom: string;
    logique: "ET" | "OU";
    conditionCodes: string[];
    rowver?: string;
}

const CODGRP_PARENT = "EXPRESSION";   // groupe parent synthetique (sous-groupes)

const Tab2 = () => {

    const [formCond] = Form.useForm();
    const authHeader = useAuthHeader();

    const [conditions, setConditions] = useState<Condition[]>([]);
    const [selectedCode, setSelectedCode] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [groupes, setGroupes] = useState<GroupeCondition[]>([]);
    const [parentId, setParentId] = useState<number>(0);
    const [parentRowver, setParentRowver] = useState<string | undefined>();
    const [logiqueEntreGroupes, setLogiqueEntreGroupes] = useState<"ET" | "OU">("ET");
    const [champsEval, setChampsEval] = useState<DataSelect[]>([]);
    const [operateurs, setOperateurs] = useState<DataSelect[]>([]);
    const [loading, setLoading] = useState(false);

    const operateurWatch = Form.useWatch("COPER", formCond);

    // ---------- appels API ----------
    const apiGet = async (url: string, headers: any = {}, method: string = 'get') => {
        const response = await axios.request({
            method, maxBodyLength: Infinity, url,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json', ...headers }
        });
        return response.data ? JSON.parse(Crypter.decrypt(response.data)) : null;
    };

    const apiSend = async (method: string, url: string, headerKey: string, payload: any) => {
        const data = Crypter.encrypt(JSON.stringify(payload));
        const response = await axios.request({
            method, maxBodyLength: Infinity, url,
            headers: { [headerKey]: data, 'Authorization': authHeader(), 'Content-Type': 'application/json' }
        });
        return response.data ? JSON.parse(Crypter.decrypt(response.data)) : null;
    };

    // ---------- chargements ----------
    const chargerReferentiels = async () => {
        try {
            const [ch, op] = await Promise.all([
                apiGet(OpeApi.ReadChampsEval),
                apiGet(OpeApi.ReadOperateursEval)
            ]);
            if (ch?.StatutCode === 200)
                setChampsEval(ch.Result.Table.map((r: any) => ({ label: `${r.CODCHAMP} — ${r.LIBCHAMP}`, value: r.CODCHAMP })));
            if (op?.StatutCode === 200)
                setOperateurs(op.Result.Table.map((r: any) => ({ label: `${r.CODOPECOMP} — ${r.LIBCOMP}`, value: r.CODOPECOMP })));
        } catch (e) { console.log("referentiels:", e); }
    };

    const chargerConditions = async () => {
        try {
            const res = await apiGet(OpeApi.ReadConditions);
            if (res?.StatutCode === 200) {
                setConditions(res.Result.Table.map((r: any) => ({
                    code: r.CODCOND, libelle: r.LIBCOND, champ: r.CHAMPEV,
                    operateur: r.OPECOMP, valeur: r.VALEUR1 ?? "", valeur2: r.VALEUR2 ?? undefined,
                    listeval: r.LISTEVAL ?? undefined, active: r.ACTIVE, rowver: r.ROWVER
                })));
            }
        } catch (e) { console.log("conditions:", e); }
    };

    const chargerGroupes = async () => {
        try {
            const res = await apiGet(OpeApi.ReadGroupes);
            if (res?.StatutCode !== 200) return;
            const heads: any[] = res.Result.Table ?? [];

            // Elements de chaque groupe (2e resultset de ReadGroupe)
            const details = await Promise.all(heads.map(h => apiGet(OpeApi.ReadGroupe, { IDGRP: h.IDGRP })));

            let parent: any = null;
            const leaves: { head: any; elements: any[] }[] = [];
            details.forEach((d, i) => {
                const elems = d?.Result?.Table1 ?? [];
                if (heads[i].CODGRP === CODGRP_PARENT) parent = { head: heads[i], elems };
                else leaves.push({ head: heads[i], elements: elems });
            });

            // Ordre des groupes feuilles = ordre des elements du parent (s'il existe)
            const ordre: number[] = parent
                ? parent.elems.sort((a: any, b: any) => a.ORDRE - b.ORDRE).map((e: any) => e.IDGRPFILS)
                : [];
            leaves.sort((a, b) => {
                const ia = ordre.indexOf(a.head.IDGRP), ib = ordre.indexOf(b.head.IDGRP);
                return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib) || a.head.CODGRP.localeCompare(b.head.CODGRP);
            });

            setGroupes(leaves.map(l => ({
                id: l.head.IDGRP, codgrp: l.head.CODGRP, nom: l.head.LIBGRP,
                logique: l.head.LOGIQUE, rowver: l.head.ROWVER,
                conditionCodes: l.elements.filter((e: any) => e.CODCOND)
                    .sort((a: any, b: any) => a.ORDRE - b.ORDRE).map((e: any) => e.CODCOND)
            })));
            if (parent) {
                setParentId(parent.head.IDGRP);
                setParentRowver(parent.head.ROWVER);
                setLogiqueEntreGroupes(parent.head.LOGIQUE);
            } else {
                setParentId(0);
                setParentRowver(undefined);
            }
        } catch (e) { console.log("groupes:", e); }
    };

    useEffect(() => {
        chargerReferentiels();
        chargerConditions();
        chargerGroupes();
    }, []);

    const conditionsFiltrees = useMemo(() =>
        conditions.filter(c =>
            (c.code + " " + c.libelle).toUpperCase().includes(search.toUpperCase())
        ), [conditions, search]);

    const exprCondition = (code: string) => {
        const c = conditions.find(x => x.code === code);
        if (!c) return `[${code} ?]`;
        if (c.operateur === "ENTRE") return `${c.champ} ENTRE ${c.valeur} ET ${c.valeur2 ?? "…"}`;
        if (c.operateur === "DANS") return `${c.champ} DANS (${c.listeval ?? "…"})`;
        return `${c.champ} ${c.operateur} ${c.valeur}`;
    };

    const expressionPreview = useMemo(() => {
        const parts = groupes
            .filter(g => g.conditionCodes.length > 0)
            .map(g => `( ${g.conditionCodes.map(exprCondition).join(` ${g.logique} `)} )`);
        return parts.length ? parts.join(` ${logiqueEntreGroupes} `) : "Aucune condition sélectionnée";
    }, [groupes, conditions, logiqueEntreGroupes]);

    const groupesDeLaCondition = (code: string | null) =>
        code ? groupes.filter(g => g.conditionCodes.includes(code)) : [];

    const resetEditor = () => {
        formCond.resetFields();
        setSelectedCode(null);
    };

    const selectCondition = (c: Condition) => {
        setSelectedCode(c.code);
        formCond.setFieldsValue({
            CCODE: c.code,
            CLIBELL: c.libelle,
            CCHAMP: c.champ,
            COPER: c.operateur,
            CVALEUR: c.valeur,
            CVALEUR2: c.valeur2 ?? "",
            CLISTEVAL: c.listeval ?? "",
            CACTIVE: c.active
        });
    };

    const conditionPayload = (v: any, rowver?: string): any => ({
        CODCOND: (v.CCODE ?? "").trim().toUpperCase(),
        LIBCOND: v.CLIBELL,
        CHAMPEV: v.CCHAMP,
        OPECOMP: v.COPER,
        VALEUR1: v.CVALEUR ?? null,
        VALEUR2: v.COPER === "ENTRE" ? v.CVALEUR2 : null,
        LISTEVAL: v.COPER === "DANS" ? v.CLISTEVAL : null,
        ACTIVE: v.CACTIVE ?? true,
        UTILIS: null,
        ROWVER: rowver ?? null
    });

    const handleEnregistrer = () => {
        formCond.validateFields().then(async (v) => {
            const existante = conditions.find(c => c.code === (v.CCODE ?? "").trim().toUpperCase());
            if (!existante) {
                const res = await apiSend('post', OpeApi.CreateCondition, 'CypherCondition', conditionPayload(v));
                if (res?.StatutCode === 200) {
                    setSelectedCode(v.CCODE.trim().toUpperCase());
                    await chargerConditions();
                    SweetAlert.fire({ title: "Condition enregistrée", text: "Code : " + v.CCODE, icon: "success" });
                } else {
                    SweetAlert.fire({ title: "Refusé", text: res?.Message ?? "Erreur", icon: "warning" });
                }
            } else {
                const usages = groupesDeLaCondition(existante.code).length;
                SweetAlert.fire({
                    title: "Mise à jour de la condition",
                    text: usages > 0
                        ? `Cette condition est utilisée par ${usages} groupe(s). La modification impactera toutes les configurations qui la référencent.`
                        : "Confirmer la mise à jour ?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#116599",
                    cancelButtonColor: "darkred",
                    confirmButtonText: "Yes",
                    cancelButtonText: "No"
                }).then(async (r) => {
                    if (r.isConfirmed) {
                        const res = await apiSend('put', OpeApi.UpdateCondition, 'CypherCondition', conditionPayload(v, existante.rowver));
                        if (res?.StatutCode === 200) {
                            await chargerConditions();
                            SweetAlert.fire({ title: "Condition mise à jour", text: "Code : " + v.CCODE, icon: "success" });
                        } else {
                            SweetAlert.fire({ title: "Refusé", text: res?.Message ?? "Erreur", icon: "warning" });
                        }
                    }
                });
            }
        }).catch(() => {
            SweetAlert.fire({ title: "Attention", text: "Veuillez remplir tous les champs obligatoires", icon: "warning" });
        });
    };

    const handleToggleActif = () => {
        if (!selectedCode) {
            SweetAlert.fire({ title: "Attention", text: "Sélectionnez une condition", icon: "warning" });
            return;
        }
        const c = conditions.find(x => x.code === selectedCode)!;
        const faire = async () => {
            const res = await apiSend('put', OpeApi.UpdateCondition, 'CypherCondition', {
                ...conditionPayload({ CCODE: c.code, CLIBELL: c.libelle, CCHAMP: c.champ, COPER: c.operateur, CVALEUR: c.valeur, CVALEUR2: c.valeur2, CLISTEVAL: c.listeval, CACTIVE: !c.active }, c.rowver)
            });
            if (res?.StatutCode === 200) {
                await chargerConditions();
                formCond.setFieldValue("CACTIVE", !c.active);
            } else {
                SweetAlert.fire({ title: "Refusé", text: res?.Message ?? "Erreur", icon: "warning" });
            }
        };
        if (c.active) {
            const usages = groupesDeLaCondition(selectedCode).length;
            SweetAlert.fire({
                title: "Désactivation de la condition",
                text: usages > 0
                    ? `Cette condition est référencée dans ${usages} groupe(s). Le serveur refusera si elle est utilisée.`
                    : "Confirmer la désactivation ?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#116599",
                cancelButtonColor: "darkred",
                confirmButtonText: "Yes",
                cancelButtonText: "No"
            }).then((r) => { if (r.isConfirmed) faire(); });
        } else {
            faire();
        }
    };

    const ajouterGroupe = () => {
        const slug = Date.now().toString(36).toUpperCase();
        setGroupes([...groupes, { id: 0, codgrp: `G_${slug}`, nom: "Groupe " + (groupes.length + 1), logique: "ET", conditionCodes: [] }]);
    };

    const retirerGroupe = (g: GroupeCondition) => {
        SweetAlert.fire({
            title: "Suppression du groupe",
            text: g.id > 0 ? "Le groupe sera supprimé définitivement en base." : "Confirmer la suppression ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#116599",
            cancelButtonColor: "darkred",
            confirmButtonText: "Yes",
            cancelButtonText: "No"
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            if (g.id === 0) {
                setGroupes(groupes.filter(x => x !== g));
                return;
            }
            try {
                const res = await apiGet(OpeApi.DeleteGroupe, { IDGRP: g.id }, 'delete');
                if (res?.StatutCode === 200) await chargerGroupes();
                else SweetAlert.fire({ title: "Refusé", text: res?.Message ?? "Erreur", icon: "warning" });
            } catch (e) {
                SweetAlert.fire({ title: "Erreur", text: String(e), icon: "error" });
            }
        });
    };

    // Sauvegarde atomique : chaque groupe puis le parent EXPRESSION (si >1)
    const sauvegarderGroupes = async () => {
        const invalides = groupes.filter(g => g.conditionCodes.length === 0);
        if (groupes.length === 0) {
            SweetAlert.fire({ title: "Attention", text: "Aucun groupe à enregistrer", icon: "warning" });
            return;
        }
        if (invalides.length > 0) {
            SweetAlert.fire({ title: "Attention", text: `« ${invalides[0].nom} » ne contient aucune condition`, icon: "warning" });
            return;
        }
        setLoading(true);
        try {
            const ids: number[] = [];
            for (const g of groupes) {
                const res = await apiSend('post', OpeApi.SaveGroupe, 'CypherGroupe', {
                    IDGRP: g.id,
                    CODGRP: g.codgrp,
                    LIBGRP: g.nom,
                    LOGIQUE: g.logique,
                    ACTIVE: true,
                    UTILIS: null,
                    ROWVER: g.rowver ?? null,
                    ELEMENTS: g.conditionCodes.map((code, i) => ({ ORDRE: i + 1, CODCOND: code, IDGRPFILS: null }))
                });
                if (res?.StatutCode !== 200) {
                    SweetAlert.fire({ title: `Refusé (${g.nom})`, text: res?.Message ?? "Erreur", icon: "warning" });
                    setLoading(false);
                    return;
                }
                ids.push(Number(res.Result));
            }
            // Parent EXPRESSION : present uniquement si plus d'un groupe
            if (ids.length > 1) {
                const res = await apiSend('post', OpeApi.SaveGroupe, 'CypherGroupe', {
                    IDGRP: parentId,
                    CODGRP: CODGRP_PARENT,
                    LIBGRP: "Expression composée",
                    LOGIQUE: logiqueEntreGroupes,
                    ACTIVE: true,
                    UTILIS: null,
                    ROWVER: parentRowver ?? null,
                    ELEMENTS: ids.map((id, i) => ({ ORDRE: i + 1, CODCOND: null, IDGRPFILS: id }))
                });
                if (res?.StatutCode !== 200) {
                    SweetAlert.fire({ title: "Refusé (expression)", text: res?.Message ?? "Erreur", icon: "warning" });
                    setLoading(false);
                    return;
                }
            }
            await chargerGroupes();
            SweetAlert.fire({ title: "Groupes enregistrés", text: `${ids.length} groupe(s)`, icon: "success" });
        } catch (e) {
            SweetAlert.fire({ title: "Erreur", text: String(e), icon: "error" });
        }
        setLoading(false);
    };

    const updateGroupe = (key: number | string, patch: Partial<GroupeCondition>) => {
        setGroupes(groupes.map(g => (g.id === key || g.codgrp === key) ? { ...g, ...patch } : g));
    };

    const colonnesConditions: ColumnsType<Condition> = [
        { title: "Code", dataIndex: "code", width: 70 },
        { title: "Libellé", dataIndex: "libelle", ellipsis: true },
        {
            title: "Expression", key: "expr", ellipsis: true,
            render: (_, c) => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{exprCondition(c.code)}</span>
        },
        {
            title: "Statut", dataIndex: "active", width: 80,
            render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>
        }
    ];

    return (
        <Form form={formCond} component={false} disabled={false}>
            <div className="tabItem" style={{ display: "block", width: "102%" }}>

                <Box className="border" width={"87%"} style={{ marginBottom: 15 }}>
                    <legend> Gestion des Conditions </legend>
                    <Box style={{ display: "flex", padding: 8 }}>

                        <Box width={"42%"} style={{ paddingRight: 8 }}>
                            <fieldset className="bordure1">
                                <legend> Conditions existantes </legend>
                                <Space direction="vertical" style={{ width: "100%", marginBottom: 6 }}>
                                    <Input.Search
                                        placeholder="Rechercher (code ou libellé)"
                                        allowClear
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <Button className="bt_add" style={{ color: "#fff" }} icon={<PlusOutlined />} onClick={resetEditor} block>
                                        Nouvelle condition
                                    </Button>
                                </Space>
                                <Table
                                    size="small"
                                    columns={colonnesConditions}
                                    dataSource={conditionsFiltrees}
                                    rowKey="code"
                                    pagination={{ pageSize: 6, size: "small" }}
                                    onRow={(record) => ({
                                        onClick: () => selectCondition(record),
                                        style: {
                                            cursor: "pointer",
                                            backgroundColor: selectedCode === record.code ? "#e6f7ff" : undefined
                                        }
                                    })}
                                />
                            </fieldset>
                        </Box>

                        <Box width={"58%"}>
                            <fieldset className="bordure1">
                                <legend> Condition </legend>
                                <Box style={{ display: "flex" }}>
                                    <Box width={"50%"}>
                                        <InputForm width={10} label="Code" type="text" name="CCODE" placeholder="EX: C01" />
                                        <SelectOption width={10} label="Champ évalué" name="CCHAMP" Option={champsEval} />
                                        <SelectOption width={10} label="Opérateur" name="COPER" Option={operateurs} />
                                    </Box>
                                    <Box width={"50%"}>
                                        <InputForm width={10} label="Libellé" type="text" name="CLIBELL" noupercase />
                                        <InputForm width={10} label={operateurWatch === "ENTRE" ? "Valeur min" : "Valeur"} type="text" name="CVALEUR" />
                                        {operateurWatch === "ENTRE" && (
                                            <InputForm width={10} label="Valeur max" type="text" name="CVALEUR2" />
                                        )}
                                        {operateurWatch === "DANS" && (
                                            <InputForm width={10} label="Liste (séparateur ';')" type="text" name="CLISTEVAL" />
                                        )}
                                        <CheckStan width={10} label="Condition active" name="CACTIVE" CheCkDefauld />
                                    </Box>
                                </Box>
                                <Box style={{ display: "flex", marginTop: 8 }}>
                                    <Space style={{ width: "100%" }}>
                                        <Button className="bt_add" style={{ color: "#fff" }} icon={<SaveOutlined />} onClick={handleEnregistrer} loading={loading}>
                                            Enregistrer
                                        </Button>
                                        <Button className="bt_add" style={{ color: "#fff" }} icon={selectedCode && conditions.find(c => c.code === selectedCode)?.active ? <StopOutlined /> : <CheckOutlined />} onClick={handleToggleActif}>
                                            {selectedCode && conditions.find(c => c.code === selectedCode)?.active ? "Désactiver" : "Activer"}
                                        </Button>
                                        <Button className="bt_add" style={{ color: "#fff" }} icon={<ReloadOutlined />} onClick={resetEditor}>
                                            Réinitialiser
                                        </Button>
                                    </Space>
                                </Box>
                            </fieldset>

                            <fieldset className="bordure1">
                                <legend> Référencée par </legend>
                                {selectedCode ? (
                                    <Box>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Groupes de conditions :</Typography.Text>
                                        <Box style={{ marginTop: 4 }}>
                                            {groupesDeLaCondition(selectedCode).length > 0
                                                ? groupesDeLaCondition(selectedCode).map(g => <Tag key={g.id || g.codgrp} color="blue">{g.nom}</Tag>)
                                                : <Tag>Aucun groupe</Tag>}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        Sélectionnez une condition pour voir ses références
                                    </Typography.Text>
                                )}
                            </fieldset>
                        </Box>
                    </Box>
                </Box>

                <Box className="border" width={"87%"}>
                    <legend> Constructeur de Groupes de Conditions </legend>
                    <Box style={{ padding: 8 }}>
                        <Box style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                            <Typography.Text style={{ marginRight: 12 }}>Logique entre groupes :</Typography.Text>
                            <Radio.Group
                                size="small"
                                optionType="button"
                                buttonStyle="solid"
                                value={logiqueEntreGroupes}
                                onChange={(e) => setLogiqueEntreGroupes(e.target.value)}
                            >
                                <Radio.Button value="ET">ET</Radio.Button>
                                <Radio.Button value="OU">OU</Radio.Button>
                            </Radio.Group>
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={ajouterGroupe}
                                style={{ marginLeft: "auto" }}
                            >
                                Nouveau groupe
                            </Button>
                            <Button
                                className="bt_add"
                                icon={<SaveOutlined />}
                                onClick={sauvegarderGroupes}
                                loading={loading}
                                style={{ marginLeft: 8, color: "#fff" }}
                            >
                                Enregistrer les groupes
                            </Button>
                        </Box>

                        {groupes.length === 0 && (
                            <Box style={{ textAlign: "center", color: "#999", padding: 12 }}>
                                Aucun groupe — cliquez sur « Nouveau groupe »
                            </Box>
                        )}

                        {groupes.map((g, idx) => (
                            <Fragment key={g.id || g.codgrp}>
                                {idx > 0 && (
                                    <Box style={{ textAlign: "center", margin: "2px 0" }}>
                                        <Tag color="blue" style={{ fontWeight: "bold" }}>{logiqueEntreGroupes}</Tag>
                                    </Box>
                                )}
                                <fieldset className="bordure1">
                                    <legend>{g.nom}</legend>
                                    <Box style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                                        <Input
                                            size="small"
                                            value={g.nom}
                                            onChange={(e) => updateGroupe(g.id || g.codgrp, { nom: e.target.value })}
                                            style={{ width: 200 }}
                                            placeholder="Nom du groupe"
                                        />
                                        <Typography.Text style={{ margin: "0 8px 0 16px", fontSize: 12 }} type="secondary">
                                            Conditions liées par :
                                        </Typography.Text>
                                        <Radio.Group
                                            size="small"
                                            optionType="button"
                                            value={g.logique}
                                            onChange={(e) => updateGroupe(g.id || g.codgrp, { logique: e.target.value })}
                                        >
                                            <Radio.Button value="ET">ET</Radio.Button>
                                            <Radio.Button value="OU">OU</Radio.Button>
                                        </Radio.Group>
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => retirerGroupe(g)}
                                            style={{ marginLeft: "auto" }}
                                        />
                                    </Box>
                                    <Select
                                        mode="multiple"
                                        style={{ width: "100%" }}
                                        size="middle"
                                        placeholder="Sélectionner les conditions du groupe"
                                        options={conditions.map(c => ({
                                            label: `${c.code} — ${c.libelle}${c.active ? "" : " (inactive)"}`,
                                            value: c.code,
                                            disabled: !c.active && !g.conditionCodes.includes(c.code)
                                        }))}
                                        value={g.conditionCodes}
                                        onChange={(v) => updateGroupe(g.id || g.codgrp, { conditionCodes: v })}
                                        optionFilterProp="label"
                                    />
                                    {g.conditionCodes.length > 0 && (
                                        <Box style={{ marginTop: 6 }}>
                                            {g.conditionCodes.map((code, i) => (
                                                <Fragment key={code}>
                                                    {i > 0 && <Tag style={{ margin: "0 2px" }}>{g.logique}</Tag>}
                                                    <Tag color="geekblue" style={{ fontFamily: "monospace", fontSize: 11 }}>
                                                        {exprCondition(code)}
                                                    </Tag>
                                                </Fragment>
                                            ))}
                                        </Box>
                                    )}
                                </fieldset>
                            </Fragment>
                        ))}

                        <Box style={{ marginTop: 12 }}>
                            <Typography.Text strong>Aperçu de l'expression :</Typography.Text>
                            <div style={{
                                fontFamily: "monospace",
                                fontSize: 12,
                                backgroundColor: "#f5f5f5",
                                border: "1px solid #d9d9d9",
                                borderRadius: 6,
                                padding: "8px 12px",
                                marginTop: 4
                            }}>
                                {expressionPreview}
                            </div>
                        </Box>
                    </Box>
                </Box>
            </div>
        </Form>
    );
};

export default Tab2;
